import type { RoleEnum } from '@zeus/index'
import type { RouteObject } from 'react-router-dom'
import { createBrowserRouter, redirect } from 'react-router-dom'
import AccountView from '~/pages/account/AccountView'
import LearnerAccountPage from '~/pages/account/LearnerAccountPage'
import App from '~/pages/App'
import ConfirmTrial from '~/pages/authentication/ConfirmTrial'
import CreateAccount from '~/pages/authentication/CreateAccount'
import Denied from '~/pages/authentication/Denied'
import KnownOrgGate from '~/pages/authentication/find-organization/KnownOrgGate'
import FindOrganization from '~/pages/authentication/FindOrganization'
import Login from '~/pages/authentication/Login'
import Logout from '~/pages/authentication/Logout'
import MagicLink from '~/pages/authentication/MagicLink'
import NewPassword from '~/pages/authentication/NewPassword'
import ResetPassword from '~/pages/authentication/ResetPassword'
import Signup from '~/pages/authentication/Signup'
import SsoCallback from '~/pages/authentication/SsoCallback'
import ValidAccount from '~/pages/authentication/ValidAccount'
import AccommodationsPage from '~/pages/backoffice/admin/accommodations/AccommodationsPage'
import BillingPage from '~/pages/backoffice/admin/billing/BillingPage'
import BrandingSettings from '~/pages/backoffice/admin/branding/BrandingSettings'
import CertificatesPage from '~/pages/backoffice/admin/certificates/CertificatesPage'
import ComplaintsPage from '~/pages/backoffice/admin/complaints/ComplaintsPage'
import DashboardPage from '~/pages/backoffice/admin/dashboard/DashboardPage'
import DocumentsPage from '~/pages/backoffice/admin/documents/DocumentsPage'
import GroupsTable from '~/pages/backoffice/admin/groups/GroupsTable'
import ModulesTable from '~/pages/backoffice/admin/modules/ModulesTable'
import OrganizationsTable from '~/pages/backoffice/admin/organizations/OrganizationsTable'
import TrialRequests from '~/pages/backoffice/admin/organizations/TrialRequests'
import QualityPage from '~/pages/backoffice/admin/quality/QualityPage'
import QuestionsPage from '~/pages/backoffice/admin/questions/QuestionsPage'
import ReminderSettings from '~/pages/backoffice/admin/reminders/ReminderSettings'
import ReportsPage from '~/pages/backoffice/admin/reports/ReportsPage'
import SessionDetailPage from '~/pages/backoffice/admin/sessions/detail/SessionDetailPage'
import SessionsTable from '~/pages/backoffice/admin/sessions/SessionsTable'
import SsoSettings from '~/pages/backoffice/admin/sso/SsoSettings'
import SurveyQuestionsPage from '~/pages/backoffice/admin/survey/SurveyQuestionsPage'
import TrainerCapabilities from '~/pages/backoffice/admin/trainer-capabilities/TrainerCapabilities'
import LearnerProfilePage from '~/pages/backoffice/admin/user/profile/LearnerProfilePage'
import UserTable from '~/pages/backoffice/admin/user/UserTable'
import WebhookSettings from '~/pages/backoffice/admin/webhooks/WebhookSettings'
import Error from '~/pages/errors/Error'
import NotFound from '~/pages/errors/NotFound'
import LearnerProfileDemo from '~/pages/help/demo/LearnerProfileDemo'
import PathDemo from '~/pages/help/demo/PathDemo'
import PlayerDemo from '~/pages/help/demo/PlayerDemo'
import PreviewDemo from '~/pages/help/demo/PreviewDemo'
import SessionDemo from '~/pages/help/demo/SessionDemo'
import HelpView from '~/pages/help/HelpView'
import LearnerHelpPage from '~/pages/help/LearnerHelpPage'
import BackOfficeLayout from '~/pages/layouts/BackOfficeLayout'
import Root from '~/pages/layouts/Root'
import MySessions from '~/pages/scorm/MySessions'
import ScormModulePreview from '~/pages/scorm/ScormModulePreview'
import ScormPlayer from '~/pages/scorm/ScormPlayer'
import ScormResume from '~/pages/scorm/ScormResume'
import SessionPath from '~/pages/scorm/SessionPath'
import { trackNavigation } from '~/tracing'
import { currentRouting } from './orgRouting'
import PlatformFallback from './PlatformFallback'
import { createProtectedLoader, routeLoader } from './protected-loader'

export type UserData = {
  firstName: string
  lastName: string
  email: string
  role: RoleEnum
  id: string
} | null

/** Redirige les anciennes URL vers le tableau de bord, query comprise. */
function legacyDashboardRedirect({ request }: { request: Request }) {
  const { search } = new URL(request.url)
  return redirect(`/back-office/dashboard${search}`)
}

// URL par organisation (#50) : toutes les routes vivent sous /o/:orgSlug via le
// basename react-router. Les chemins absolus internes (navigate, Link) sont donc
// automatiquement préfixés — aucune adaptation de la navigation nécessaire. Les
// deux routes publiques d'acquisition font exception et sont servies à la racine
// (#37) : `orgRouting.ts` porte la règle et ses cas limites.
const routing = currentRouting()
if (routing.redirectTo) window.location.replace(routing.redirectTo)

/**
 * Routeur des seules routes publiques, monté à la racine. Il réutilise `Root`
 * pour que `/signup` et `/o/default/signup` rendent strictement la même chose
 * (bandeau d'impersonation, notice de mesure d'audience).
 */
const platformRoutes: RouteObject[] = [
  {
    path: '/',
    Component: Root,
    errorElement: <Error />,
    children: [
      { path: 'signup', Component: Signup, handle: { title: 'pageTitle.signup' } },
      { path: 'confirm-trial', Component: ConfirmTrial, handle: { title: 'pageTitle.confirmTrial' } },
      // Entrée de connexion de la landing (#346) : le visiteur nomme son org.
      { path: 'login', Component: FindOrganization, handle: { title: 'pageTitle.findOrganization' } },
      // La racine du domaine mène à l'entrée de connexion (#346). Le splat ne
      // couvre PAS le segment vide : sans cette route d'index, `/` matcherait le
      // layout seul et rendrait un écran blanc. Épinglé dans `orgRouting.spec.ts`.
      { index: true, loader: () => redirect('/login') },
      { path: '*', Component: PlatformFallback },
    ],
  },
]

const appRoutes: RouteObject[] = [
  {
    path: '/',
    Component: Root,
    errorElement: <Error />,
    children: [
      {
        index: true,
        Component: App,
        loader: createProtectedLoader([]),
      },
      { path: 'logout', Component: Logout, handle: { title: 'pageTitle.logout' } },
      { path: 'reset-password', Component: ResetPassword, handle: { title: 'pageTitle.resetPassword' } },
      { path: 'valid-account', Component: ValidAccount, handle: { title: 'pageTitle.validAccount' } },
      { path: 'new-password', Component: NewPassword, handle: { title: 'pageTitle.newPassword' } },
      { path: 'create-account', Component: CreateAccount, handle: { title: 'pageTitle.createAccount' } },
      { path: 'magic-link', Component: MagicLink, handle: { title: 'pageTitle.magicLink' } },
      { path: 'sso', Component: SsoCallback, handle: { title: 'pageTitle.sso' } },
      { path: 'denied', Component: Denied, handle: { title: 'pageTitle.denied' } },
      { path: 'login', element: <KnownOrgGate><Login /></KnownOrgGate>, handle: { title: 'pageTitle.login' } },
      // Parcours d'essai en libre-service (#155) — publiques, sans loader.
      { path: 'signup', Component: Signup, handle: { title: 'pageTitle.signup' } },
      { path: 'confirm-trial', Component: ConfirmTrial, handle: { title: 'pageTitle.confirmTrial' } },
      // « Mon compte » côté apprenant (#77). Le back-office a le sien, dans son
      // layout : chaque menu mène à celui de son cadre.
      {
        path: 'account',
        Component: LearnerAccountPage,
        handle: { title: 'common.routing.account' },
        loader: routeLoader('account'),
      },
      // Centre d'aide côté apprenant (#78) ; le back-office a le sien.
      {
        path: 'help',
        Component: LearnerHelpPage,
        handle: { title: 'common.routing.help' },
        loader: routeLoader('help'),
      },
      // Démonstrations du centre d'aide (#105) : les écrans à id, sur données
      // fictives, pour que leurs visites aient une adresse fixe et rien à écrire.
      {
        path: 'help/demo/path',
        Component: PathDemo,
        handle: { title: 'pageTitle.demoPath' },
        loader: routeLoader('help/demo/path'),
      },
      {
        path: 'help/demo/player',
        Component: PlayerDemo,
        handle: { title: 'pageTitle.demoPlayer' },
        loader: routeLoader('help/demo/player'),
      },
      // Mêmes droits que la vraie prévisualisation.
      {
        path: 'help/demo/preview',
        Component: PreviewDemo,
        handle: { title: 'pageTitle.demoPreview' },
        loader: routeLoader('help/demo/preview'),
      },
      {
        path: 'my-sessions',
        Component: MySessions,
        handle: { title: 'home.title' },
        loader: routeLoader('my-sessions'),
      },
      // L'écran d'un parcours (#74). Distinct de `scorm/:sessionId`, qui LANCE le
      // prochain module : celui-ci montre le parcours sans rien démarrer.
      {
        path: 'my-sessions/:sessionId',
        Component: SessionPath,
        handle: { title: 'pageTitle.path' },
        loader: routeLoader('my-sessions/:sessionId'),
      },
      {
        path: 'scorm/preview/:moduleId',
        Component: ScormModulePreview,
        handle: { title: 'pageTitle.preview' },
        loader: routeLoader('scorm/preview/:moduleId'),
      },
      // ⚠️ La route SANS module est conservée, et ce n'est pas de la
      // complaisance : c'est celle que portent les liens de rappel par mail
      // (`reminders.service.ts`), déjà partis dans des boîtes. Elle redirige
      // vers le prochain module ouvert et non terminé, ce qui est exactement le
      // sens voulu — « reprends où tu en étais » (ADR 0002 § 7.3).
      {
        path: 'scorm/:sessionId',
        Component: ScormResume,
        handle: { title: 'pageTitle.player' },
        loader: routeLoader('scorm/:sessionId'),
      },
      {
        path: 'scorm/:sessionId/:moduleId',
        Component: ScormPlayer,
        handle: { title: 'pageTitle.player' },
        loader: routeLoader('scorm/:sessionId/:moduleId'),
      },
      {
        path: 'back-office',
        Component: BackOfficeLayout,
        handle: { title: 'common.back-office' },
        children: [
          // Accueil et statistiques ne font plus qu'un écran : les deux
          // répondaient à « où en est-on ? », et il fallait ouvrir l'un pour
          // comprendre l'autre. Ouvert au formateur, qui n'y voit pas la même
          // population que l'admin — la borne est posée côté API
          // (`scorm-tracking.service.ts`).
          {
            path: 'dashboard',
            Component: DashboardPage,
            handle: { title: 'common.routing.dashboard' },
            loader: routeLoader('back-office/dashboard'),
          },
          // Réponses aux questions des modules (#159) : même accès que les statistiques.
          {
            path: 'questions',
            Component: QuestionsPage,
            handle: { title: 'common.routing.questions' },
            loader: routeLoader('back-office/questions'),
          },
          // Rapports configurables (#163) : le formateur télécharge, l'admin compose aussi.
          {
            path: 'reports',
            Component: ReportsPage,
            handle: { title: 'common.routing.reports' },
            loader: routeLoader('back-office/reports'),
          },
          // Les deux anciennes URL restent vivantes : elles sont dans les
          // favoris, et `?sessionId=` circule dans des liens déjà partagés. La
          // redirection garde la query, sans quoi le filtre serait perdu au
          // passage — un lien qui ouvre le mauvais périmètre est pire qu'un
          // lien mort, il ne se voit pas.
          {
            path: 'account',
            Component: AccountView,
            handle: { title: 'common.routing.account' },
            loader: routeLoader('back-office/account'),
          },
          {
            path: 'help',
            Component: HelpView,
            handle: { title: 'common.routing.help' },
            loader: routeLoader('back-office/help'),
          },
          // La fiche session en démonstration (#243) : sa visite a besoin d'une
          // adresse fixe, la vraie fiche porte un id.
          {
            path: 'help/demo/session',
            Component: SessionDemo,
            handle: { title: 'pageTitle.demoSession' },
            loader: routeLoader('back-office/help/demo/session'),
          },
          { path: 'home', loader: legacyDashboardRedirect },
          { path: 'statistics', loader: legacyDashboardRedirect },
          {
            path: 'modules',
            Component: ModulesTable,
            handle: { title: 'common.routing.modules' },
            loader: routeLoader('back-office/modules'),
          },
          {
            path: 'sessions',
            Component: SessionsTable,
            handle: { title: 'common.routing.sessions' },
            loader: routeLoader('back-office/sessions'),
          },
          // Fiche d'une session (#243), en lecture : même accès que la liste.
          {
            path: 'sessions/:sessionId',
            Component: SessionDetailPage,
            handle: { title: 'common.routing.sessionDetail' },
            loader: routeLoader('back-office/sessions/:sessionId'),
          },
          // Ouverte au formateur, qui y suit ses apprenants. Il n'y voit pas la
          // même population que l'admin : `getFilteredUsers` borne la liste sur
          // le rôle du porteur du JWT (users.service.ts).
          {
            path: 'users',
            Component: UserTable,
            handle: { title: 'common.routing.users' },
            loader: routeLoader('back-office/users'),
          },
          {
            path: 'users/:userId',
            Component: LearnerProfilePage,
            handle: { title: 'common.routing.learnerProfile' },
            loader: routeLoader('back-office/users/:userId'),
          },
          // La fiche sur une personne fictive : la visite guidée a besoin d'une adresse sans id.
          {
            path: 'help/demo/learner',
            Component: LearnerProfileDemo,
            handle: { title: 'common.routing.learnerProfile' },
            loader: routeLoader('back-office/help/demo/learner'),
          },
          {
            path: 'groups',
            Component: GroupsTable,
            handle: { title: 'common.routing.groups' },
            loader: routeLoader('back-office/groups'),
          },
          {
            path: 'certificates',
            Component: CertificatesPage,
            handle: { title: 'common.routing.certificates' },
            loader: routeLoader('back-office/certificates'),
          },
          {
            path: 'accommodations',
            Component: AccommodationsPage,
            handle: { title: 'common.routing.accommodations' },
            loader: routeLoader('back-office/accommodations'),
          },
          {
            path: 'complaints',
            Component: ComplaintsPage,
            handle: { title: 'common.routing.complaints' },
            loader: routeLoader('back-office/complaints'),
          },
          {
            path: 'documents',
            Component: DocumentsPage,
            handle: { title: 'common.routing.documents' },
            loader: routeLoader('back-office/documents'),
          },
          {
            path: 'quality',
            Component: QualityPage,
            handle: { title: 'common.routing.quality' },
            loader: routeLoader('back-office/quality'),
          },
          {
            path: 'survey',
            Component: SurveyQuestionsPage,
            handle: { title: 'common.routing.survey' },
            loader: routeLoader('back-office/survey'),
          },
          {
            path: 'reminders',
            Component: ReminderSettings,
            handle: { title: 'common.routing.reminders' },
            loader: routeLoader('back-office/reminders'),
          },
          {
            path: 'settings',
            Component: BrandingSettings,
            handle: { title: 'common.routing.settings' },
            loader: routeLoader('back-office/settings'),
          },
          {
            path: 'sso',
            Component: SsoSettings,
            handle: { title: 'common.routing.sso' },
            loader: routeLoader('back-office/sso'),
          },
          {
            path: 'webhooks',
            Component: WebhookSettings,
            handle: { title: 'common.routing.webhooks' },
            loader: routeLoader('back-office/webhooks'),
          },
          {
            path: 'trainer-capabilities',
            Component: TrainerCapabilities,
            handle: { title: 'common.routing.trainer-capabilities' },
            loader: routeLoader('back-office/trainer-capabilities'),
          },
          {
            path: 'organizations',
            Component: OrganizationsTable,
            handle: { title: 'common.routing.organizations' },
            loader: routeLoader('back-office/organizations'),
          },
          {
            path: 'trial-requests',
            Component: TrialRequests,
            handle: { title: 'common.routing.trial-requests' },
            loader: routeLoader('back-office/trial-requests'),
          },
          {
            path: 'billing',
            Component: BillingPage,
            handle: { title: 'common.routing.billing' },
            loader: routeLoader('back-office/billing'),
          },
        ],
      },
      { path: 'error', Component: Error, loader: createProtectedLoader([]), handle: { title: 'pageTitle.error' } },
      { path: '*', Component: NotFound, loader: createProtectedLoader([]), handle: { title: 'pageTitle.notFound' } },
    ],
  },
]

export const router = createBrowserRouter(
  routing.mode === 'platform' ? platformRoutes : appRoutes,
  { basename: routing.basename },
)

let lastTrackedKey = ''
router.subscribe((state) => {
  if (state.navigation.state !== 'idle') return
  const { pathname, search } = state.location
  const key = `${pathname}${search}`
  if (key === lastTrackedKey) return
  lastTrackedKey = key
  trackNavigation(pathname, search)
})
